import { useCallback, useState } from "react";

import { getDB } from "features/medical-scribe/medical-scribe-db/medical-scribe-db.util";

type UseScribeAudioDownloadParams = {
  remoteMedicalScribeId: number | undefined;
  localId: string | undefined;
  remoteRecordingUrl: string | undefined;
};

type UseScribeAudioDownloadResult = {
  download: () => Promise<void>;
  isDownloading: boolean;
};

export function useScribeAudioDownload({
  remoteMedicalScribeId,
  localId,
  remoteRecordingUrl
}: UseScribeAudioDownloadParams): UseScribeAudioDownloadResult {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadRemoteFallback = useCallback(
    async (recordingUrl: string, scribeId: number | undefined) => {
      const response = await fetch(recordingUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const urlPath = new URL(recordingUrl).pathname;
      const filename =
        urlPath.split("/").pop() || `scribe-recording-${scribeId}.webm`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    []
  );

  const download = useCallback(async () => {
    try {
      setIsDownloading(true);

      if (!remoteMedicalScribeId && !localId) {
        console.error("No scribe ID or recording ID found");
        return;
      }

      const db = await getDB();
      let audioRecordingId: string | null = null;
      let recordingMetadata: any = null;

      if (remoteMedicalScribeId) {
        // Streaming mode: find the recording by medical_transcribe_id
        const tx = db.transaction("recordings", "readonly");
        const store = tx.objectStore("recordings");
        const allRecordings = await store.getAll();

        for (const recording of allRecordings) {
          if (recording.medical_transcribe_id === remoteMedicalScribeId) {
            audioRecordingId = recording.id;
            recordingMetadata = recording;
            break;
          }
        }

        if (!audioRecordingId) {
          console.log(
            "useScribeAudioDownload: Streaming mode - no local recording found for medical_transcribe_id:",
            remoteMedicalScribeId,
            "- will try remote fallback"
          );
        }
      } else if (localId) {
        // Non-streaming mode: localId IS the audio recording ID
        audioRecordingId = localId;
        const recording = await db.get("recordings", localId);
        recordingMetadata = recording;
      }

      if (!audioRecordingId) {
        if (remoteRecordingUrl) {
          await downloadRemoteFallback(
            remoteRecordingUrl,
            remoteMedicalScribeId
          );
        }
        return;
      }

      const chunksRecord = await db.get("recordings_chunks", audioRecordingId);

      if (!chunksRecord?.chunks?.length) {
        console.error("No audio chunks found for recording:", audioRecordingId);
        if (remoteRecordingUrl) {
          await downloadRemoteFallback(
            remoteRecordingUrl,
            remoteMedicalScribeId
          );
        }
        return;
      }

      let audioBlob: Blob;
      let fileExtension: string;

      const wasRecordedInStreamingMode =
        recordingMetadata?.isStreaming ?? false;

      if (wasRecordedInStreamingMode) {
        // Streaming mode: each chunk is a complete WAV file — extract PCM data and combine
        const pcmDataArrays: ArrayBuffer[] = [];
        let totalPcmSize = 0;

        for (const chunk of chunksRecord.chunks) {
          const arrayBuffer = await chunk.arrayBuffer();
          // Skip the 44-byte WAV header and keep PCM data
          const pcmData = arrayBuffer.slice(44);
          pcmDataArrays.push(pcmData);
          totalPcmSize += pcmData.byteLength;
        }

        // Build a single WAV header for the combined PCM stream
        const wavHeader = new ArrayBuffer(44);
        const view = new DataView(wavHeader);

        const sampleRate = 16000;
        const numChannels = 1;
        const bitsPerSample = 16;
        const bytesPerSample = bitsPerSample / 8;
        const blockAlign = numChannels * bytesPerSample;
        const byteRate = sampleRate * blockAlign;

        const writeString = (offset: number, str: string) => {
          for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.codePointAt(i) || 0);
          }
        };

        writeString(0, "RIFF");
        view.setUint32(4, 36 + totalPcmSize, true);
        writeString(8, "WAVE");
        writeString(12, "fmt ");
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitsPerSample, true);
        writeString(36, "data");
        view.setUint32(40, totalPcmSize, true);

        audioBlob = new Blob([wavHeader, ...pcmDataArrays], {
          type: "audio/wav"
        });
        fileExtension = "wav";
      } else {
        // Non-streaming mode: chunks are WebM, concatenate directly
        audioBlob = new Blob(chunksRecord.chunks, { type: "audio/webm" });
        fileExtension = "webm";
      }

      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scribe-recording-${remoteMedicalScribeId || audioRecordingId}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading local audio:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [
    remoteMedicalScribeId,
    localId,
    remoteRecordingUrl,
    downloadRemoteFallback
  ]);

  return { download, isDownloading };
}
